import IconButton from '@/components/ui/custom/buttons/IconButton';
import SaveStatus from '@/components/ui/custom/dataDisplay/SaveStatus';
import ModalBank from '@/components/ui/custom/modals/ModalBank/ModalBank';
import { useActivityTool } from '@/data/hook/editorjs/useActivityTool';
import useCourseEditing from '@/data/hook/useCourseEditing';
import useSafePortal from '@/data/hook/useSafePortal';
import useToggle from '@/data/hook/useToggle';
import { ChapterRouteParams } from '@/data/services/sectionServices';
import Activity from '@/models/_deprecated/Activity';
import { Section } from '@/models/Book';
import { ImageFolderEnum } from '@/models/CkEditor';
import { ActivityQuiz } from '@/utils/editorjs/activity-block/adapter/activityToQuizAdapter';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '../../../../ConditionalRenderer';
import MaterialEditorJS from '../MaterialEditorJS';
import SectionContainer from '../view/SectionContainer';
import SectionActivityFormModal from './SectionActivityFormModal';
import SectionTitleForm from './SectionTitleForm';
import StartHiddenInput from './StartsHiddenInput';

export type SectionFormFields = Pick<
  Section,
  'content' | 'title' | 'icon' | 'startsHidden' | 'activitiesIds'
>;

type SectionFormProps = {
  section: Section;
  isDeleting?: boolean;
  isLoading?: boolean;
  hasChanges?: boolean;
  onDelete: () => void;
  onSubmit: (params: {
    section: SectionFormFields;
    validated: boolean;
  }) => void;
} & ChapterRouteParams;

export default function SectionForm({
  section,
  onSubmit,
  onDelete,
  isLoading,
  isDeleting,
  hasChanges,
  ...routeParams
}: SectionFormProps) {
  const createSafePortal = useSafePortal();
  const { t } = useTranslation('translation', {
    keyPrefix: 'section.form',
  });

  const { t: tPage } = useTranslation('translation', {
    keyPrefix: 'editMaterialPage',
  });

  const activityId = useRef<number>();
  const { allowBigChanges } = useCourseEditing();

  const { watch, control, setValue } = useForm<SectionFormFields>({
    defaultValues: {
      content: section.content,
      title: section.title,
      icon: section.icon,
      startsHidden: section.startsHidden,
      activitiesIds: section.activitiesIds,
    },
  });

  function onSaveActivity(activity: ActivityQuiz) {
    setActivity(activity);
    activityId.current = undefined;
    setValue('activitiesIds', [...watch('activitiesIds'), activity.id]);
  }

  const onUseQuestion = (activity: Activity) => {
    setActivity(activity as ActivityQuiz);
    activityId.current = undefined;
    setValue('activitiesIds', [...watch('activitiesIds'), activity.id]);
    closeModalBank();
  };

  const { isOpen, toggle, close } = useToggle();
  const {
    isOpen: isOpenModalBank,
    toggle: toggleModalBank,
    close: closeModalBank,
  } = useToggle();

  function onEditActivity(activityIdParam: number) {
    activityId.current = activityIdParam;
    toggle();
  }

  const onClose = () => {
    clearPromise();
    close();
    closeModalBank();
    activityId.current = undefined;
  };

  const { setActivity, tool, clearPromise } = useActivityTool({
    onAddActivity: toggle,
    onEditActivity,
    onSelectActivity: toggleModalBank,
  });

  useEffect(() => {
    const subscription = watch(e =>
      onSubmit({ section: e as SectionFormFields, validated: true }),
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, onSubmit]);

  return (
    <SectionContainer className="w-full h-full">
      <div className="relative" role="form" data-testid="sectionForm">
        <div className="flex gap-2.5 items-center flex-wrap">
          <SectionTitleForm
            control={control}
            onClose={() => {
              setValue('title', section.title);
            }}
          />
          {/* TODO: enable when has web socket back impl */}
          <ConditionalRenderer condition={false}>
            <Controller
              control={control}
              name="startsHidden"
              render={({ field: { value, onChange } }) => (
                <StartHiddenInput value={value} onChange={onChange} />
              )}
            />
          </ConditionalRenderer>
        </div>
        <Controller
          control={control}
          name="content"
          render={({ field: { value, onChange } }) => (
            <MaterialEditorJS
              folder={ImageFolderEnum.BOOK}
              value={value}
              onChange={onChange}
              extraBody={routeParams}
              minHeight={0}
              activityBlock={tool}
            />
          )}
        />

        {createSafePortal(
          <SectionActivityFormModal
            sectionParams={{ ...routeParams, sectionId: section.id }}
            onClose={onClose}
            isVisible={isOpen}
            activityId={activityId.current}
            onSaveActivity={onSaveActivity}
          />,
        )}
        {createSafePortal(
          <ModalBank
            initial="questions"
            onUseQuestion={onUseQuestion}
            visible={isOpenModalBank}
            onClose={onClose}
            isSlideMaterial
          />,
        )}

        <div className="relative ml-auto w-fit sm:absolute top-0 right-0 flex gap-2">
          <SaveStatus isLoading={isLoading} hasChanges={hasChanges} />
          <IconButton
            onClick={onDelete}
            type="button"
            loading={isDeleting}
            className="w-5 h-5 text-error"
            testId="deleteSection"
            tooltip={allowBigChanges ? t('deleteSection') : tPage('cantModify')}
            disabled={!allowBigChanges}
            icon={<TrashIcon />}
          />
        </div>
      </div>
    </SectionContainer>
  );
}
